import React, { useEffect, useState } from "react";
import clsx from "clsx";
import {
  Box,
  Card,
  Grid,
  makeStyles,
  FormControl,
  TextField,
  Button,
  FormHelperText,
} from "@material-ui/core";
import SaveIcon from "@material-ui/icons/Save";
import { NavLink as RouterLink } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import { kmMessages } from "src/constant/Messages";
import { DateTimePicker } from "@material-ui/pickers";
import Skeleton from "@material-ui/lab/Skeleton";
import moment from "moment";
import { fillMissingTags } from "src/utils/utils";

const nameMaxLength = 300;
const urlMaxLength = 512;
const urllabelMaxLength = 128;

const formValidation = {
  required: kmMessages.error.form_validation.field_required,
  maxLength: `${kmMessages.error.form_validation.field_maxlengthnum}`,
  dateChoice: `${kmMessages.error.form_validation.field_dateonlychoice}`,
};

const useStyles = makeStyles((theme) => ({
  root: {
    "& > *": {
      margin: theme.spacing(2),
    },
  },
  formcontrol: {
    marginBottom: theme.spacing(2),
    marginTop: theme.spacing(2),
  },
  paddingbottom: {
    paddingBottom: theme.spacing(1),
  },
  paddingtop: {
    paddingTop: theme.spacing(2),
  },
  button: {
    margin: theme.spacing(1),
  },
}));

const Form = ({
  formReady,
  onSubmit,
  calendarEvent,
  editMode,
  calendarEventStartDate,
  setCalendarEventStartDate,
  calendarEventEndDate,
  setCalendarEventEndDate,
  ...rest
}) => {
  const classes = useStyles();

  const [afterSubmit, setAfterSubmit] = useState(false);

  const { handleSubmit, setValue, control, errors } = useForm();

  useEffect(() => {
    if (editMode && calendarEvent) {
      if (calendarEvent.startsAt) {
        setCalendarEventStartDate(moment(calendarEvent.startsAt));
      }
      if (calendarEvent.endsAt) {
        setCalendarEventEndDate(moment(calendarEvent.endsAt));
      }
      setValue("name", calendarEvent.name);
      setValue("url", calendarEvent.url);
      setValue("urllabel", calendarEvent.urlLabel);

    }
    return () => {};
  }, [calendarEvent]);

  useEffect(() => {
    setTimeout(fillMissingTags(), 1000);
    return () => {};
  }, []);

  const handleCalendarEventStartDateChange = (date) => {
    setCalendarEventStartDate(date);
  };

  const handleCalendarEventEndDateChange = (date) => {
    setCalendarEventEndDate(date);
  };

  const internalOnSubmit = (values) => {
    setAfterSubmit(true);
    if (calendarEventStartDate && calendarEventEndDate) {
      onSubmit(values);
    }
  };

  return (
    <Card className={clsx(classes.root)} {...rest}>
      <Box component="main">
        <Grid container className={classes.root}>
          <Grid item xs={12} elevation={2}>
            <form onSubmit={handleSubmit(internalOnSubmit)}>
              {formReady ? (
                <FormControl fullWidth className={classes.formcontrol}>
                  <Controller
                    as={
                      <TextField
                        autoFocus
                        error={errors.name ? true : false}
                        id="name"
                        name="name"
                        label="Nazwa"
                        placeholder="Nazwa"
                        multiline
                        helperText={
                          errors.name &&
                          (formValidation[errors.name.type] || "").replaceAll(
                            "{maxlen}",
                            nameMaxLength
                          )
                        }
                      />
                    }
                    name="name"
                    defaultValue={calendarEvent ? calendarEvent.name : ""}
                    control={control}
                    rules={{ required: true, maxLength: nameMaxLength }}
                  />
                </FormControl>
              ) : (
                <Skeleton />
              )}
              {formReady ? (
                <FormControl fullWidth className={classes.formcontrol}>
                  <DateTimePicker
                    error={afterSubmit && !calendarEventStartDate}
                    clearable
                    autoOk
                    ampm={false}
                    id="calendareventstartdate"
                    fullWidth
                    label="Data i godzina rozpoczęcia"
                    clearable
                    value={calendarEventStartDate}
                    placeholder="Data i godzina rozpoczęcia"
                    onChange={(date) =>
                      handleCalendarEventStartDateChange(date)
                    }
                    format="DD/MM/yyyy HH:mm"
                    animateYearScrolling
                  />

                  <FormHelperText id="calendarevent-end-date" error>
                    {afterSubmit && !calendarEventStartDate
                      ? formValidation.dateChoice
                      : ""}
                  </FormHelperText>
                </FormControl>
              ) : (
                <Skeleton />
              )}
              {formReady ? (
                <FormControl fullWidth className={classes.formcontrol}>
                  <DateTimePicker
                    error={afterSubmit && !calendarEventEndDate}
                    clearable
                    autoOk
                    ampm={false}
                    id="calendareventenddate"
                    fullWidth
                    label="Data i godzina zakończenia"
                    clearable
                    value={calendarEventEndDate}
                    placeholder="Data i godzina zakończenia"
                    onChange={(date) => handleCalendarEventEndDateChange(date)}
                    format="DD/MM/yyyy HH:mm"
                    animateYearScrolling
                  />

                  <FormHelperText id="calendarevent-end-date" error>
                    {afterSubmit && !calendarEventEndDate
                      ? formValidation.dateChoice
                      : ""}
                  </FormHelperText>
                </FormControl>
              ) : (
                <Skeleton />
              )}
              {formReady ? (
                <FormControl fullWidth className={classes.formcontrol}>
                  <Controller
                    as={
                      <TextField
                        autoFocus
                        error={errors.url ? true : false}
                        id="url"
                        name="url"
                        label="Odnośnik"
                        placeholder="Odnośnik"
                        multiline
                        helperText={
                          errors.url &&
                          (formValidation[errors.url.type] || "").replaceAll(
                            "{maxlen}",
                            urlMaxLength
                          )
                        }
                      />
                    }
                    name="url"
                    defaultValue={calendarEvent ? calendarEvent.url : ""}
                    control={control}
                    rules={{ maxLength: urlMaxLength }}
                  />
                </FormControl>
              ) : (
                <Skeleton />
              )}
              {formReady ? (
                <FormControl fullWidth className={classes.formcontrol}>
                  <Controller
                    as={
                      <TextField
                        autoFocus
                        error={errors.urllabel ? true : false}
                        id="urllabel"
                        name="urllabel"
                        label="Etykieta odnośnika"
                        placeholder="Etykieta odnośnika"
                        multiline
                        helperText={
                          errors.urllabel &&
                          (
                            formValidation[errors.urllabel.type] || ""
                          ).replaceAll("{maxlen}", urllabelMaxLength)
                        }
                      />
                    }
                    name="urllabel"
                    defaultValue={calendarEvent ? calendarEvent.urlLabel : ""}
                    control={control}
                    rules={{ maxLength: urllabelMaxLength }}
                  />
                </FormControl>
              ) : (
                <Skeleton />
              )}
              <Box className={classes.paddingtop}>
                <Button
                  color="primary"
                  className={classes.button}
                  component={RouterLink}
                  to={`/kalendarz/zarzadzaniewydarzenia`}
                >
                  Anuluj
                </Button>
                <Button
                  disabled={!formReady}
                  color="primary"
                  variant="contained"
                  className={classes.button}
                  endIcon={<SaveIcon />}
                  type="submit"
                >
                  Zapisz
                </Button>
              </Box>
            </form>
          </Grid>
        </Grid>
      </Box>
    </Card>
  );
};

export default Form;
